<template>
  <div class="container mt-4">
    <TopNav
      :title="$t('GENERAL.LABEL.QUIZZES')"
      :showOpenMainNav="true"
      :icon="['far', 'chart-bar']"
    >
      <NavItem
        icon="fal fa-plus"
        :text="$t('QUIZZES.LABEL.CREATE_QUIZ')"
        @click="createQuiz"
      />
    </TopNav>
    <div v-if="quizzesStats" class="row no-gutters my-4">
      <HMStatCard
        class="col-12 col-md mt-2 mt-md-0 mr-md-4"
        :title="$t('QUIZZES.LABEL.TOTAL_QUIZZES')"
        :clickable="false"
        :content="quizzesStats.total"
      />
      <HMStatCard
        class="col-12 col-md mt-2 mt-md-0 mr-md-4"
        :title="$t('QUIZZES.LABEL.SUBMISSIONS')"
        :clickable="false"
        :content="quizzesStats.submitted"
      />
      <HMStatCard
        class="col-12 col-md mt-2 mt-md-0 mr-md-4"
        :title="$t('QUIZZES.LABEL.PASSED')"
        :clickable="false"
        :content="quizzesStats.passed"
      />
      <HMStatCard
        class="col-12 col-md mt-2 mt-md-0 mr-md-4"
        :title="$t('QUIZZES.LABEL.FAILED')"
        :clickable="false"
        :content="quizzesStats.failed"
      />
    </div>
    <div class="row no-gutters my-4">
      <HMTable
        class="mt-3 w-100"
        :items="quizzes"
        :fields="quizzesFields"
        v-if="quizzes"
      >
        <template slot="quiz_name" slot-scope="{ data }">
          <h5 @click="openQuiz(data.id)" class="m-0 c-coral pointer">
            {{ data.quiz_name || $t('QUIZZES.TITLE.NEW_QUIZ') }}
          </h5>
        </template>
        <template slot="created" slot-scope="{ data }">
          {{ $formatDate(data.created, 'date-word') }}
        </template>
        <template slot="course_number" slot-scope="{ data }">
          {{ data.number_of_courses }}
        </template>
        <template slot="responses" slot-scope="{ data }">
          {{ data.number_of_responses }}
          <span v-if="data.number_of_responses_today > 0" class="c-charcoal">
            {{
              $t('QUIZZES.TEXT.TODAY', {
                count: data.number_of_responses_today,
              })
            }}
          </span>
        </template>
        <template slot="needs-grading" slot-scope="{ data }">
          <HMTag v-if="data.needs_grading > 0">
            {{
              $tc('QUIZZES.TEXT.NEEDS_REVIEW_VARIANT', data.needs_grading, {
                count: data.needs_grading,
              })
            }}
          </HMTag>
        </template>
        <template slot="actions" slot-scope="{ data }">
          <HMIconButton
            :icon="['far', 'ellipsis-v']"
            :id="`popover-${data.id}`"
          />
          <HMPopover :target="`popover-${data.id}`" boundary-padding="-20">
            <HMPopoverMenuItem @click="openQuiz(data.id)">
              {{ $t('QUIZZES.LINK.VIEW_QUIZ') }}
            </HMPopoverMenuItem>
            <HMPopoverMenuItem @click="copyLink(data.id)">
              {{ $t('QUIZZES.LINK.COPY_LINK') }}
            </HMPopoverMenuItem>
            <HMPopoverMenuItem
              v-confirm="{
                title: $t('QUIZZES.LINK.DELETE_QUIZ'),
                text: $t('QUIZZES.TEXT.DELETE_QUIZ_DESCRIPTION'),
                confirmButton: $t('QUIZZES.LINK.DELETE_QUIZ'),
                confirm: () => deleteQuiz(data),
              }"
            >
              {{ $t('QUIZZES.LINK.DELETE_QUIZ') }}
            </HMPopoverMenuItem>
          </HMPopover>
        </template>
        <template v-slot:no-data>
          <HMEmpty
            image="empty-orders-v2"
            :title="$t('QUIZZES.EMPTY.TITLE')"
            :description="$t('QUIZZES.EMPTY.DESCRIPTION')"
          />
        </template>
      </HMTable>
    </div>
  </div>
</template>

<script>
import Vue from 'vue';
import TopNav from '@/web/components/navigation/TopNav.vue';
import NavItem from '@/web/components/navigation/NavItem.vue';
import { mapGetters } from 'vuex';
import { errorToMessage } from '@/core/utils/errors';
import { getStudentViewURL } from '@/web/router/custom-domain-utils';
import { notify } from '@/web/utils/notify';

export default Vue.extend({
  name: 'Quizzes',
  components: {
    TopNav,
    NavItem,
  },
  data() {
    return {
      quizzesFields: [
        {
          name: 'quiz_name',
          title: this.$t('QUIZZES.TABLE.NAME'),
        },
        {
          name: 'created',
          title: this.$t('QUIZZES.TABLE.DATE_CREATED'),
        },
        {
          name: 'course_number',
          title: this.$t('QUIZZES.TABLE.COURSE_NUMBER'),
        },
        {
          name: 'responses',
          title: this.$t('QUIZZES.TABLE.RESPONSES'),
        },
        {
          name: 'needs-grading',
          title: '',
        },
        {
          name: 'actions',
          title: '',
        },
      ],
      quizzesStats: null,
    };
  },
  computed: {
    ...mapGetters({
      quizzes: 'quiz/quizzes',
      teacherStudioUrl: 'user/teacherStudioUrl',
    }),
  },
  async mounted() {
    try {
      await this.$store.dispatch('quiz/refreshQuizzes');
      this.quizzesStats = await this.$store.dispatch('quiz/getQuizzesStats');
    } catch (error) {
      this.$notify({
        group: 'app',
        type: 'error',
        title: this.$t('NOTIFICATIONS.TITLE.ERROR'),
        text: errorToMessage(
          error?.response,
          `${this.$t(
            'QUIZZES.NOTIFICATIONS.FAILED_TO_LOAD_QUIZ_STATS'
          )}: ${error}`
        ),
        duration: -1, // stay ON
      });
    }
  },
  methods: {
    openQuiz(id) {
      this.$router.push({ name: 'quiz', params: { id } });
    },
    copyLink(id) {
      const url = getStudentViewURL(this.teacherStudioUrl, `/quiz/${id}`, null);
      this.$copyText(url).then(() => {
        notify(this.$t('NOTIFICATIONS.TEXT.COPIED'), url);
      });
    },
    async createQuiz() {
      try {
        const { id } = await this.$store.dispatch('quiz/createQuiz');
        this.$router.push({
          name: 'quiz-settings',
          params: { id, hideOverview: true },
        });

        this.$notify({
          group: 'app',
          type: 'success',
          title: this.$t('NOTIFICATIONS.TITLE.SUCCESS'),
          text: this.$t('QUIZZES.MODAL.QUIZ_CREATED_DESCRIPTION'),
        });
      } catch (error) {
        this.$notify({
          group: 'app',
          type: 'error',
          title: this.$t('NOTIFICATIONS.TITLE.ERROR').toString(),
          text: errorToMessage(
            error.response,
            `${this.$t(
              'QUIZZES.NOTIFICATIONS.FAILED_TO_CREATE_QUIZ'
            )}: ${error}`
          ),
          duration: -1,
        });
      }
    },
    async deleteQuiz(quiz) {
      try {
        await this.$store.dispatch('quiz/deleteQuiz', quiz.id);

        this.$notify({
          group: 'app',
          type: 'success',
          title: this.$t('NOTIFICATIONS.TITLE.SUCCESS'),
          text: this.$t('QUIZZES.MODAL.QUIZ_DELETED_DESCRIPTION'),
        });

        await this.$store.dispatch('quiz/refreshQuizzes');
      } catch (error) {
        this.$notify({
          group: 'app',
          type: 'error',
          title: this.$t('NOTIFICATIONS.TITLE.ERROR').toString(),
          text: errorToMessage(
            error.response,
            `${this.$t(
              'QUIZZES.NOTIFICATIONS.FAILED_TO_DELETE_QUIZ'
            )}: ${error}`
          ),
          duration: -1,
        });
      }
    },
  },
});
</script>
